/**
 * Suivis d'un agent — M22 (retour Manon, mi-parcours).
 *
 * « Sur le dossier de l'agent, entamer un circuit de décision paramétrable —
 *   suivi médical, suivi de dossier — avec une raison, un objectif et des
 *   canaux de rappel paramétrables. »
 *
 * Chaque suivi : type, raison, objectif ; un CIRCUIT facultatif (les circuits
 * actifs de la commune, réglés dans Administration → Circuits, avec leurs
 * canaux par étape) et une DATE BUTOIR facultative (échéance avec rappels).
 * Le bloc de circuit est générique : il servira aux autres entités à la
 * refonte UX.
 */
import { useCallback, useEffect, useState } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button, Input, Badge } from '@/components/ui';
import { CircuitFrise } from '@/components/circuits/CircuitFrise';
import { api, ApiException, type SuiviAgentDTO, type TypeSuivi, type WorkflowInstance } from '@/lib/api';

const TYPES: Record<TypeSuivi, string> = {
  suivi_medical: 'Suivi médical', suivi_dossier: 'Suivi de dossier', entretien: 'Entretien',
  accompagnement: 'Accompagnement', disciplinaire: 'Procédure disciplinaire', autre: 'Autre',
};
const fr = (iso: string) => new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** Bloc de circuit générique : état courant, flèches autorisées, historique. */
export function CircuitBloc({ instanceId, onChange }: { instanceId: string; onChange: () => void }) {
  const [inst, setInst] = useState<WorkflowInstance | null>(null);
  const [histo, setHisto] = useState<Awaited<ReturnType<typeof api.workflows.historique>>['evenements']>([]);
  const [commentaire, setCommentaire] = useState('');
  const [piece, setPiece] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      const [i, h] = await Promise.all([api.workflows.instance(instanceId), api.workflows.historique(instanceId)]);
      setInst(i); setHisto(h.evenements);
    } catch { setInst(null); }
  }, [instanceId]);
  useEffect(() => { void charger(); }, [charger]);

  async function franchir(transitionId: string, pieceRequise: boolean) {
    if (pieceRequise && !piece) { setErr('Cette étape exige une pièce (PDF ou photo)'); return; }
    setErr(null);
    try {
      await api.workflows.transiterInstance(instanceId, transitionId, { commentaire: commentaire.trim() || undefined, piece: piece ?? undefined });
      setCommentaire(''); setPiece(null); await charger(); onChange();
    } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Étape impossible'); }
  }

  if (!inst) return null;
  const autorisees = inst.transitionsDisponibles.filter((t) => t.autorisee);
  return (
    <div className="mt-2 rounded-r-md border border-border/60 p-2.5">
      <p className="text-xs text-ink-muted">{inst.definitionLibelle} · étape : <b className="text-ink">{inst.etat.libelle}</b>{inst.etat.estFinal ? ' (terminé)' : ''}</p>
      {/* M7 — la frise : chaque étape, qui, quand, et sa pièce. */}
      {inst.etats && inst.etats.length > 0 && (
        <CircuitFrise instanceId={instanceId} etats={inst.etats} etatCourantId={inst.etat.id} historique={histo} />
      )}
      {err && <p className="mt-1 text-xs text-urgent" role="alert">{err}</p>}
      {autorisees.length > 0 && !inst.etat.estFinal && (
        <div className="mt-2 space-y-2">
          <Input value={commentaire} maxLength={2000} placeholder="Commentaire (facultatif)" aria-label="Commentaire de l'étape"
            onChange={(e) => setCommentaire(e.target.value)} />
          {autorisees.some((t) => t.pieceRequise) && (
            <input type="file" accept="application/pdf,image/*" aria-label="Pièce de l'étape"
              className="block w-full text-xs text-ink-muted" onChange={(e) => setPiece(e.target.files?.[0] ?? null)} />
          )}
          <div className="flex flex-wrap gap-1.5">
            {autorisees.map((t) => (
              <Button key={t.id} size="sm" variant="rh" onClick={() => void franchir(t.id, t.pieceRequise)}>
                {t.libelle}{t.pieceRequise ? ' 📎' : ''}
              </Button>
            ))}
          </div>
        </div>
      )}
      {histo.length > 0 && (
        <ul className="mt-2 space-y-0.5 border-t border-border/50 pt-1.5">
          {histo.map((h) => (
            <li key={h.id} className="text-2xs text-ink-subtle">
              {fr(h.createdAt)} — {h.versEtat}{h.par ? ` · ${h.par}` : ''}{h.commentaire ? ` · « ${h.commentaire} »` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SuivisAgentDialog({ agentId, agentNom, onClose }: { agentId: string; agentNom: string; onClose: () => void }) {
  const [suivis, setSuivis] = useState<SuiviAgentDTO[]>([]);
  const [circuits, setCircuits] = useState<Array<{ id: string; libelle: string }>>([]);
  const [form, setForm] = useState(false);
  const [type, setType] = useState<TypeSuivi>('suivi_medical');
  const [raison, setRaison] = useState(''); const [objectif, setObjectif] = useState('');
  const [circuitId, setCircuitId] = useState('');
  const [date, setDate] = useState(''); const [rappelJours, setRappelJours] = useState(7);
  const [prevenirAgent, setPrevenirAgent] = useState(true);
  const [conclusions, setConclusions] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try { setSuivis(await api.rh.suivisAgent(agentId)); } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Chargement impossible'); }
  }, [agentId]);
  useEffect(() => {
    void charger();
    api.workflows.circuitsActifs().then((r) => setCircuits(r.circuits)).catch(() => setCircuits([]));
  }, [charger]);

  async function creer() {
    if (raison.trim().length < 3) { setErr('Indiquez la raison du suivi'); return; }
    setEnvoi(true); setErr(null);
    try {
      await api.rh.creerSuiviAgent(agentId, {
        type, raison: raison.trim(), objectif: objectif.trim() || null, circuitId: circuitId || null,
        echeance: date ? { date, rappels: rappelJours > 0 ? [{ quantite: rappelJours, unite: 'jours' }] : [], destinataireIds: [], prevenirAgent } : null,
      });
      setForm(false); setRaison(''); setObjectif(''); setCircuitId(''); setDate('');
      await charger();
    } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Création impossible'); }
    finally { setEnvoi(false); }
  }

  async function clore(id: string) {
    try { await api.rh.cloreSuivi(id, conclusions[id]?.trim() || null); await charger(); }
    catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Clôture impossible'); }
  }

  return (
    <Dialog open onClose={onClose} title={`Suivis — ${agentNom}`} size="lg"
      description="Suivi médical, de dossier, entretien… avec une raison, un objectif, un circuit de décision et une date butoir.">
      <div className="space-y-3">
        {err && <p className="text-xs text-urgent" role="alert">{err}</p>}
        {!form && <Button size="sm" variant="rh" onClick={() => setForm(true)}>Nouveau suivi</Button>}

        {form && (
          <div className="space-y-2 rounded-r-lg border border-border/60 p-3">
            <label className="block text-xs text-ink">Type
              <select className="mt-1 h-10 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={type}
                onChange={(e) => setType(e.target.value as TypeSuivi)}>
                {(Object.keys(TYPES) as TypeSuivi[]).map((t) => <option key={t} value={t}>{TYPES[t]}</option>)}
              </select>
            </label>
            <label className="block text-xs text-ink">Raison
              <Input className="mt-1" value={raison} maxLength={2000} placeholder="Ex : retour après un arrêt long" onChange={(e) => setRaison(e.target.value)} />
            </label>
            <label className="block text-xs text-ink">Objectif (facultatif)
              <Input className="mt-1" value={objectif} maxLength={2000} placeholder="Ex : aménagement du poste" onChange={(e) => setObjectif(e.target.value)} />
            </label>
            <label className="block text-xs text-ink">Circuit de décision (facultatif)
              <select className="mt-1 h-10 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={circuitId}
                onChange={(e) => setCircuitId(e.target.value)}>
                <option value="">— Aucun circuit —</option>
                {circuits.map((c) => <option key={c.id} value={c.id}>{c.libelle}</option>)}
              </select>
              <span className="mt-0.5 block text-2xs text-ink-subtle">Les étapes et leurs canaux (cloche, e-mail, courrier) se règlent dans Administration → Circuits.</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs text-ink">Date butoir (facultative)
                <Input className="mt-1" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </label>
              <label className="block text-xs text-ink">Rappel (jours avant)
                <Input className="mt-1" type="number" min={0} max={999} value={rappelJours} disabled={!date}
                  onChange={(e) => setRappelJours(Math.max(0, Number(e.target.value) || 0))} />
              </label>
            </div>
            {date && (
              <label className="flex items-center gap-2 text-xs text-ink">
                <input type="checkbox" checked={prevenirAgent} onChange={(e) => setPrevenirAgent(e.target.checked)} />
                Prévenir aussi l'agent (s'il a un compte)
              </label>
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="rh" loading={envoi} onClick={() => void creer()}>Créer le suivi</Button>
              <Button size="sm" variant="ghost" onClick={() => setForm(false)}>Annuler</Button>
            </div>
          </div>
        )}

        {suivis.length === 0 && !form && <p className="text-sm text-ink-subtle">Aucun suivi pour cet agent.</p>}
        <ul className="space-y-2">
          {suivis.map((s) => (
            <li key={s.id} className="rounded-r-md border border-border/60 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <b className="text-ink">{TYPES[s.type]}</b>
                <Badge variant={s.statut === 'en_cours' ? 'warning' : 'success'} size="sm">{s.statut === 'en_cours' ? 'En cours' : 'Clos'}</Badge>
                {s.echeance && <Badge variant="neutral" size="sm">Butoir : {fr(s.echeance.date)}</Badge>}
                <span className="ml-auto text-2xs text-ink-subtle">ouvert le {fr(s.createdAt)}{s.creeParNom ? ` par ${s.creeParNom}` : ''}</span>
              </div>
              <p className="mt-1 text-xs text-ink-muted">Raison : {s.raison}{s.objectif ? ` · Objectif : ${s.objectif}` : ''}</p>
              {s.conclusion && <p className="text-xs text-ink-muted">Conclusion : {s.conclusion}</p>}
              {s.circuit && <CircuitBloc instanceId={s.circuit.instanceId} onChange={() => void charger()} />}
              {s.statut === 'en_cours' && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input className="min-w-[200px] flex-1" value={conclusions[s.id] ?? ''} maxLength={2000} placeholder="Conclusion (facultative)"
                    aria-label="Conclusion du suivi" onChange={(e) => setConclusions((c) => ({ ...c, [s.id]: e.target.value }))} />
                  <Button size="sm" variant="ghost" onClick={() => void clore(s.id)}>Clore le suivi</Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}
